import { addYears } from 'date-fns'

import { getT, getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'

import { AllInstrumentsSchemaType } from '@/pages/instruments/new-instrument/schemas'
import { BaseStructuredSchemaType } from '@/pages/instruments/new-instrument/schemas/shared/base-structured.schema'
import { BaseVanillaSchemaType } from '@/pages/instruments/new-instrument/schemas/shared/base-vanilla.schema'
import { BaseInstrumentSchemaType } from '@/pages/instruments/new-instrument/schemas/shared/base.schema'

const t = getTMinus1()

const baseValues: Partial<BaseInstrumentSchemaType> = {
  database: 'both',
  unit_multiplier: 1.0,
  quote_factor: 1.0,
}

const baseStructuredDefaultValues: Partial<BaseStructuredSchemaType> = {
  ...baseValues,
  currency_id: 2,
  counterparty_id: 1,
  initial_observation_date: toISO8601(new Date(t)),
  maturity_date: toISO8601(addYears(new Date(t), 2)),
  settlement_cycle: 2,
  underlyings: [{ value: 100, percent: 100 }],
  notional: null,
  sedol: null,
  isin: null,
}

const baseVanillaDefaultValues: Partial<BaseVanillaSchemaType> = {
  ...baseValues,
  currency_id: 2,
  counterparty_id: 1,
  initial_observation_date: toISO8601(new Date(t)),
  maturity_date: toISO8601(addYears(new Date(t), 1)),
  settlement_cycle: 2,
  underlyings: [{ value: 100, percent: 100 }],
  comment: 'Diversifier Swap',
  bucket_id: null,
}

export const defaultValues: {
  [K in AllInstrumentsSchemaType['instrument_type']]?: Partial<
    Extract<AllInstrumentsSchemaType, { instrument_type: K }>
  >
} = {
  aswap: {
    ...baseValues,
    fund_id: 1,
    currency_id: 2,
    effective_date: toISO8601(getT()),
    maturity_date: toISO8601(addYears(new Date(t), 1)),
    bank_legs: [
      // @ts-ignore
      { floater_spread: 12, swap_payments_yearly: 4, funding_instrument: 'SOFRRATE Index' },
    ],
  },
  bond: {
    ...baseValues,
    currency_id: 2,
    maturity_date: toISO8601(addYears(new Date(t), 1)),
    country_id: 234,
    top_level_bucket_id: null,
  },
  'diver-otc': {
    ...baseValues,
    fund_id: 1,
    currency_id: 2,
    effective_date: toISO8601(getT()),
    maturity_date: toISO8601(addYears(new Date(t), 1)),
    funding_instrument: 'SONIO/N Index',
    leverage: 1,
    notional: null,
    price_type: 'notional',
    floater_spread_bps: 12,
    swap_payments_yearly: 4,
    trade_id: null,
    bucket_id: null,
  },
  equity: {
    ...baseValues,
    currency_id: 2,
    counterparty_id: 33,
    country_id: 234,
  },
  'equity-otc': {
    ...baseValues,
    fund_id: 1,
    currency_id: 2,
    effective_date: toISO8601(getT()),
    maturity_date: toISO8601(addYears(new Date(t), 1)),
    funding_instrument: 'SONIO/N Index',
    floater_spread_bps: 12,
    swap_payments_yearly: 4,
  },
  fund: {
    ...baseValues,
    currency_id: 2,
    country_id: 234,
    hedge_currency_id: null,
    distribution_policy: 'distributed',
    issuer: 'FORTEM CAPITAL',
    administrator: null,
  },
  fxfwd: {
    ...baseValues,
    currency_from_id: 2,
    currency_quote_id: 1,
    maturity_date: toISO8601(addYears(new Date(t), 1)),
    counterparty_id: 21,
    top_level_bucket_id: null,
  },
  index: {
    ...baseValues,
    index_type: 'Equity',
    country_id: 234,
    currency_id: 2,
    isin: null,
    asset_class: null,
    factor: null,
    sub_factor: null,
    axe_flag: null,
  },
  'autocall-standard': {
    ...baseStructuredDefaultValues,
    autocall_yearly_observations: 1,
    coupon_yearly_observations: 1,
    coupon_rate_annual: 6.5,
    autocall_barrier_level: 100,
    autocall_barrier_step: -10,
    knockdown_input_level: 65,
  },
  'autocall-consolation': {
    ...baseStructuredDefaultValues,
    autocall_yearly_observations: 1,
    coupon_rate_annual: 7.25,
    autocall_barrier_level: 100,
    payoff_barrier_level: 60,
    knockdown_input_level: 60,
    autocall_barrier_step: -5,
  },
  'autocall-phoenix': {
    ...baseStructuredDefaultValues,
    autocall_yearly_observations: 1,
    coupon_yearly_observations: 4,
    coupon_rate_annual: 6.5,
    autocall_barrier_level: 100,
    autocall_barrier_step: -5,
    knockdown_input_level: 65,
    payoff_barrier_level: 65,
    payoff_barrier_step: -5,
  },
  digital: {
    ...baseStructuredDefaultValues,
    contract_type: 'Call',
    side: 'Long',
    coupon_rate_annual: 6.5,
    payoff_barrier_level: 65,
    knockdown_input_level: 65,
  },
  'vanilla-spread': {
    ...baseStructuredDefaultValues,
    contract_type: 'Call',
    contract_side: 'longLowerShortUpper',
    participation_rate: 1.5,
    knockdown_input_level: 65,
  },
  'vanilla-structured': {
    ...baseStructuredDefaultValues,
    contract_type: 'Put',
    participation_rate: 1,
    knockdown_input_level: 100,
  },
  'range-accrual': {
    ...baseStructuredDefaultValues,
    coupon_yearly_observations: 4,
    contract_type: 'Call',
    coupon_rate_annual: 6.5,
    knockdown_input_level: 65,
  },
  supertracker: {
    ...baseStructuredDefaultValues,
    participation_rate: 350,
    knockdown_input_level: 65,
  },
  vanilla: {
    ...baseVanillaDefaultValues,
    contract_type: 'Call',
    side: 'Long',
  },
  'vanilla-digital': {
    ...baseVanillaDefaultValues,
    contract_type: 'Call',
    side: 'Long',
    asset_identifier: 'Digital',
    coupon_rate_annual: 6.5,
    payoff_barrier_level: 100,
    knockdown_input_level: 65,
  },
  other: {
    ...baseStructuredDefaultValues,
  },
}
